/**
 * Zone Editor — trace tenant spaces onto the floor plan once.
 *
 * Workflow: pick a level (Lower/Upper), pick the suite you're about to draw
 * from the dropdown, then click points on the plan to draw its polygon.
 * Double-click (or "Finish Shape") closes it and saves to D1. Existing zones
 * render faintly so you can see what's already done. This is an admin tool —
 * supervisors+ only — used to build the data the Activity Map renders.
 *
 * Points are stored as percentages of the image (x,y in 0..100) so they scale
 * to any display size.
 */
import { useState, useEffect, useMemo, useRef } from 'react'
import { useCadStore } from '../../stores/cad'
import { workerGetV1, workerPutV1 } from '../../api/worker'

interface Pt { x: number; y: number }
interface Zone { wing: string; space: string; level: string; points: string }

export default function ZoneEditor() {
  const { businesses, loadRefs } = useCadStore()
  const [level, setLevel] = useState<'L' | 'U'>('L')
  const [zones, setZones] = useState<Zone[]>([])
  const [selectedSuite, setSelectedSuite] = useState('') // "wing|space"
  const [draft, setDraft] = useState<Pt[]>([])
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')
  const imgWrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => { loadRefs() }, [loadRefs])
  useEffect(() => { void reloadZones() }, [])

  async function reloadZones() {
    try {
      const res = await workerGetV1<{ ok: boolean; zones: Zone[] }>('/api/v1/cad/zones')
      setZones(res.zones || [])
    } catch { /* ignore */ }
  }

  // Suites for the active level (level derived from address U/L suffix)
  const suites = useMemo(() => {
    return businesses
      .filter(b => {
        const lvl = (b.address || '').toUpperCase().includes('U') ? 'U' : 'L'
        return lvl === level && b.wing && b.space
      })
      .map(b => ({ key: `${b.wing}|${b.space}`, label: `${b.wing}-${b.space} · ${b.name}`, wing: b.wing!, space: b.space! }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [businesses, level])

  const doneKeys = useMemo(
    () => new Set(zones.filter(z => z.level === level).map(z => `${z.wing}|${z.space}`)),
    [zones, level],
  )

  function onPlanClick(e: React.MouseEvent) {
    if (!selectedSuite) { setMsg('Pick a suite first.'); return }
    const wrap = imgWrapRef.current
    if (!wrap) return
    const rect = wrap.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100
    setDraft(d => [...d, { x: +x.toFixed(2), y: +y.toFixed(2) }])
  }

  async function finishShape() {
    if (!selectedSuite || draft.length < 3) { setMsg('Need a suite and at least 3 points.'); return }
    const [wing, space] = selectedSuite.split('|')
    setSaving(true)
    try {
      await workerPutV1('/api/v1/cad/zones', { wing, space, level, points: draft })
      setMsg(`Saved ${wing}-${space}.`)
      setDraft([])
      await reloadZones()
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Save failed')
    }
    setSaving(false)
  }

  const img = level === 'L' ? '/floor-lower.png' : '/floor-upper.png'
  const toPoly = (pts: Pt[]) => pts.map(p => `${p.x},${p.y}`).join(' ')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0 10px', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 18, color: 'var(--text-primary)' }}>Zone Editor</h1>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Pick a suite, click points on the plan to trace it, then Finish Shape</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {(['L', 'U'] as const).map(l => (
            <button key={l} onClick={() => { setLevel(l); setDraft([]) }}
              style={{ padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, border: '1px solid var(--border)',
                background: level === l ? 'var(--accent)' : 'var(--bg-elevated)', color: level === l ? '#fff' : 'var(--text-primary)' }}>
              {l === 'L' ? 'Lower' : 'Upper'}
            </button>
          ))}
          <select value={selectedSuite} onChange={e => { setSelectedSuite(e.target.value); setDraft([]) }}
            style={{ padding: '7px 10px', background: 'var(--bg-deep)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, minWidth: 220 }}>
            <option value="">— pick suite to trace —</option>
            {suites.map(s => <option key={s.key} value={s.key}>{doneKeys.has(s.key) ? '✓ ' : ''}{s.label}</option>)}
          </select>
          <button onClick={finishShape} disabled={saving || draft.length < 3}
            style={{ padding: '7px 14px', borderRadius: 6, cursor: draft.length < 3 ? 'default' : 'pointer', fontSize: 13, fontWeight: 600,
              background: draft.length >= 3 ? 'var(--accent)' : 'var(--bg-elevated)', color: draft.length >= 3 ? '#fff' : 'var(--text-muted)', border: 'none', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Finish Shape'}
          </button>
          {draft.length > 0 && (
            <button onClick={() => setDraft([])} style={{ padding: '7px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}>
              Clear ({draft.length})
            </button>
          )}
        </div>
      </div>

      {msg && <div style={{ fontSize: 12, color: 'var(--accent)', marginBottom: 6 }}>{msg}</div>}

      <div style={{ position: 'relative', flex: 1, minHeight: 0, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
        <div ref={imgWrapRef} onClick={onPlanClick} onDoubleClick={finishShape}
          style={{ position: 'relative', display: 'inline-block', cursor: selectedSuite ? 'crosshair' : 'default' }}>
          <img src={img} alt="floor plan" style={{ display: 'block', maxWidth: '100%', userSelect: 'none' }} draggable={false} />
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
            {/* existing zones for this level */}
            {zones.filter(z => z.level === level).map((z, i) => {
              let pts: Pt[] = []
              try { pts = JSON.parse(z.points) } catch { /* ignore */ }
              if (pts.length < 3) return null
              return (
                <g key={i}>
                  <polygon points={toPoly(pts)} fill="rgba(63,174,106,0.18)" stroke="rgba(63,174,106,0.8)" strokeWidth="0.2" />
                  <text x={pts[0].x} y={pts[0].y} fontSize="1.4" fill="var(--text-primary)">{z.wing}-{z.space}</text>
                </g>
              )
            })}
            {/* draft in progress */}
            {draft.length > 0 && (
              <polygon points={toPoly(draft)} fill="rgba(224,90,90,0.25)" stroke="#e05a5a" strokeWidth="0.3" />
            )}
            {draft.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="0.5" fill="#e05a5a" />)}
          </svg>
        </div>
      </div>
    </div>
  )
}
